// PaiementsPage.jsx - Version refondue avec design de SalesPage
import React, { useEffect, useState, useMemo } from "react";
import salesStore from "../../store/salesStore";
import {
  BiCalendar,
  BiReset,
  BiSearch,
  BiChevronLeft,
  BiChevronRight,
  BiDetail,
  BiPhone,
  BiReceipt,
  BiCreditCard,
  BiUser,
} from "react-icons/bi";
import { FaEnvelope, FaWhatsapp } from "react-icons/fa";
import {
  getClientContact,
  getClientName as resolveClientName,
  getWhatsappLink,
} from "../../utils/clientInfo";

// Composant Modal de Détails
function PaymentDetailModal({ isOpen, onClose, payment }) {
  if (!isOpen || !payment) return null;

  // Déterminer le client
  const client = getClientContact(payment);
  const paymentType = payment.is_vente
    ? "Vente en boutique"
    : "Paiement en ligne";
  const paymentSource = payment.appointment
    ? "Réservation"
    : payment.subscription
      ? "Abonnement"
      : "Paiement direct";

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white w-full max-w-4xl flex flex-col max-h-[90vh]">
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h2 className="font-poppins font-semibold text-xl text-title">
              Détails du paiement #{payment.code}
            </h2>
            <div className="flex items-center gap-2 mt-1">
              <span className="px-3 py-1 text-sm font-medium bg-blue-100 text-blue-800 flex items-center gap-1">
                <BiCreditCard />
                {paymentType}
              </span>
              <span className="px-3 py-1 text-sm font-medium bg-purple-100 text-purple-800">
                {paymentSource}
              </span>
              <span
                className={`px-3 py-1 text-sm font-medium ${
                  payment.method === "CASH"
                    ? "bg-green-100 text-green-800"
                    : payment.method === "CARD"
                      ? "bg-secondary-100 text-secondary-800"
                      : payment.method === "FEDAPAY"
                        ? "bg-blue-100 text-blue-800"
                        : "bg-purple-100 text-purple-800"
                }`}
              >
                {payment.method}
              </span>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl"
          >
            ×
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Informations client */}
            <div className="bg-gray-50 p-4">
              <h3 className="font-poppins font-semibold text-lg text-title mb-3">
                Client
              </h3>
              {client ? (
                <div className="space-y-3">
                  <div>
                    <p className="font-nunito text-sm text-text">Nom complet</p>
                    <p className="font-poppins font-medium">
                      {client.name || "Client non renseigné"}
                    </p>
                  </div>
                  <div>
                    <p className="font-nunito text-sm text-text">Contact</p>
                    <div className="flex gap-3 mt-2">
                      {client.email && (
                        <a
                          href={`mailto:${client.email}`}
                          className="text-gray-600 hover:text-gray-800"
                          title="Envoyer un email"
                        >
                          <FaEnvelope size={20} />
                        </a>
                      )}
                      {client.telephone && (
                        <a
                          href={`tel:${client.telephone}`}
                          className="text-secondary-600 hover:text-secondary-800"
                          title="Appeler"
                        >
                          <BiPhone size={20} />
                        </a>
                      )}
                      {(client.whatsapp || client.telephone) && (
                        <a
                          href={getWhatsappLink(client)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-green-600 hover:text-green-800"
                          title="WhatsApp"
                        >
                          <FaWhatsapp size={20} />
                        </a>
                      )}
                    </div>
                  </div>
                  {client.email && (
                    <div>
                      <p className="font-nunito text-sm text-text">Email</p>
                      <p className="font-poppins font-medium">{client.email}</p>
                    </div>
                  )}
                  {client.telephone && (
                    <div>
                      <p className="font-nunito text-sm text-text">Téléphone</p>
                      <p className="font-poppins font-medium">
                        {client.telephone}
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <p className="font-nunito text-text">Client non spécifié</p>
              )}
            </div>

            {/* Informations paiement */}
            <div className="bg-gray-50 p-4">
              <h3 className="font-poppins font-semibold text-lg text-title mb-3">
                Paiement
              </h3>
              <div className="space-y-3">
                <div>
                  <p className="font-nunito text-sm text-text">Montant</p>
                  <p className="font-poppins font-bold text-xl text-montant">
                    {parseFloat(payment.amount).toLocaleString()} FCFA
                  </p>
                </div>
                <div>
                  <p className="font-nunito text-sm text-text">Date</p>
                  <p className="font-poppins font-medium">
                    {new Date(payment.created_at).toLocaleString("fr-FR")}
                  </p>
                </div>
                <div>
                  <p className="font-nunito text-sm text-text">Statut</p>
                  <span
                    className={`px-2 py-1 text-sm ${
                      payment.status === "COMPLETED"
                        ? "bg-green-100 text-green-800"
                        : payment.status === "PENDING"
                          ? "bg-yellow-100 text-yellow-800"
                          : "bg-red-100 text-red-800"
                    }`}
                  >
                    {payment.status === "COMPLETED"
                      ? "Complété"
                      : payment.status === "PENDING"
                        ? "En attente"
                        : "Échoué"}
                  </span>
                </div>
                {payment.transactionId && (
                  <div>
                    <p className="font-nunito text-sm text-text">
                      ID Transaction
                    </p>
                    <p className="font-poppins font-medium text-sm break-all">
                      {payment.transactionId}
                    </p>
                  </div>
                )}
                {payment.code && (
                  <div>
                    <p className="font-nunito text-sm text-text">
                      Code paiement
                    </p>
                    <p className="font-poppins font-medium">{payment.code}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Informations réservation/abonnement */}
            {(payment.appointment || payment.subscription) && (
              <div className="md:col-span-2 bg-gray-50 p-4">
                <h3 className="font-poppins font-semibold text-lg text-title mb-3">
                  {payment.appointment ? "Réservation" : "Abonnement"}
                </h3>
                {payment.appointment ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="font-nunito text-sm text-text">
                        Code réservation
                      </p>
                      <p className="font-poppins font-medium">
                        {payment.appointment.code}
                      </p>
                    </div>
                    <div>
                      <p className="font-nunito text-sm text-text">
                        Date réservation
                      </p>
                      <p className="font-poppins font-medium">
                        {new Date(payment.appointment.date).toLocaleString(
                          "fr-FR",
                        )}
                      </p>
                    </div>
                    <div>
                      <p className="font-nunito text-sm text-text">Statut</p>
                      <span className="px-2 py-1 text-sm bg-green-100 text-green-800">
                        {payment.appointment.status}
                      </span>
                    </div>
                    {payment.appointment.estimatedDuration && (
                      <div>
                        <p className="font-nunito text-sm text-text">
                          Durée estimée
                        </p>
                        <p className="font-poppins font-medium">
                          {payment.appointment.estimatedDuration} minutes
                        </p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="font-nunito text-sm text-text">
                        Code abonnement
                      </p>
                      <p className="font-poppins font-medium">
                        {payment.subscription.code}
                      </p>
                    </div>
                    <div>
                      <p className="font-nunito text-sm text-text">Statut</p>
                      <span className="px-2 py-1 text-sm bg-green-100 text-green-800">
                        {payment.subscription.status}
                      </span>
                    </div>
                    {payment.subscription.end_at && (
                      <div>
                        <p className="font-nunito text-sm text-text">
                          Date de fin
                        </p>
                        <p className="font-poppins font-medium">
                          {new Date(
                            payment.subscription.end_at,
                          ).toLocaleDateString("fr-FR")}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Services */}
            <div className="md:col-span-2">
              <h3 className="font-poppins font-semibold text-lg text-title mb-3">
                Services ({payment.services?.length || 0})
              </h3>
              <div className="space-y-3">
                {payment.services?.map((item, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 bg-white border"
                  >
                    <div className="flex-1">
                      <p className="font-poppins font-medium">
                        {item.service?.nom || "Service"}
                      </p>
                      {item.service?.description && (
                        <p className="font-nunito text-sm text-text mt-1">
                          {item.service.description}
                        </p>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="font-poppins font-bold text-montant">
                        {parseFloat(item.unitPrice).toLocaleString()} FCFA
                      </p>
                      <p className="font-nunito text-sm text-text">
                        Quantité: {item.quantity}
                      </p>
                      <p className="font-nunito text-sm text-text">
                        Total:{" "}
                        {(
                          parseFloat(item.unitPrice) * item.quantity
                        ).toLocaleString()}{" "}
                        FCFA
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="border-t p-4 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 font-nunito hover:bg-gray-50"
          >
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
}

// Composant Card pour mobile
function PaymentCard({ payment, onViewDetails }) {
  // Déterminer le client
  const client = getClientContact(payment);
  const clientName = resolveClientName(payment, "Client non spécifié");

  const displayName =
    clientName.length > 20 ? clientName.substring(0, 20) + "..." : clientName;

  const paymentType = payment.is_vente ? "Vente" : "En ligne";
  const paymentSource = payment.appointment
    ? "Réservation"
    : payment.subscription
      ? "Abonnement"
      : "Direct";

  return (
    <div className="bg-white border border-gray-200 overflow-hidden hover:shadow-sm transition-shadow">
      {/* Ligne 1: Nom client + Type */}
      <div className="p-4 border-b border-gray-100">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <span
              className="font-nunito font-medium text-sm truncate"
              title={clientName}
            >
              {displayName}
            </span>
            <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 flex items-center gap-1 shrink-0">
              <BiCreditCard size={12} />
              {paymentType}
            </span>
          </div>
          <span className="px-2 py-1 text-xs bg-purple-100 text-purple-800 shrink-0">
            {paymentSource}
          </span>
        </div>

        {/* Ligne 2: Montant + Méthode */}
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <BiCreditCard className="text-gray-400" size={16} />
            <span className="font-poppins font-bold text-base text-montant">
              {parseFloat(payment.amount).toLocaleString()} FCFA
            </span>
          </div>
          <span
            className={`px-2 py-1 text-xs ${
              payment.method === "CASH"
                ? "bg-green-100 text-green-800"
                : payment.method === "CARD"
                  ? "bg-secondary-100 text-secondary-800"
                  : payment.method === "FEDAPAY"
                    ? "bg-blue-100 text-blue-800"
                    : "bg-purple-100 text-purple-800"
            }`}
          >
            {payment.method}
          </span>
        </div>
      </div>

      {/* Ligne 3: Services + Date + Contacts */}
      <div className="p-4 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1">
              <BiReceipt size={14} className="text-gray-500" />
              <span className="font-nunito text-xs text-text">
                {payment.services?.length || 0} service
                {payment.services?.length !== 1 ? "s" : ""}
              </span>
            </div>

            <div className="flex items-center gap-1">
              <BiCalendar size={14} className="text-gray-500" />
              <span className="font-nunito text-xs text-text">
                {new Date(payment.created_at).toLocaleDateString("fr-FR", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                })}
              </span>
            </div>
          </div>

          <div className="flex gap-2">
            {client && client.email && (
              <a
                href={`mailto:${client.email}`}
                className="text-gray-500 hover:text-gray-700"
                title="Envoyer un email"
              >
                <FaEnvelope size={14} />
              </a>
            )}
            {client && client.telephone && (
              <a
                href={`tel:${client.telephone}`}
                className="text-secondary-500 hover:text-secondary-700"
                title="Appeler"
              >
                <BiPhone size={14} />
              </a>
            )}
            {client && (client.whatsapp || client.telephone) && (
              <a
                href={getWhatsappLink(client)}
                target="_blank"
                rel="noopener noreferrer"
                className="text-green-500 hover:text-green-700"
                title="WhatsApp"
              >
                <FaWhatsapp size={14} />
              </a>
            )}
          </div>
        </div>
      </div>

      {/* Ligne 4: Statut + Bouton Détails */}
      <div className="p-3 bg-gray-50 flex items-center justify-between">
        <span
          className={`px-2 py-1 text-xs ${
            payment.status === "COMPLETED"
              ? "bg-green-100 text-green-800"
              : payment.status === "PENDING"
                ? "bg-yellow-100 text-yellow-800"
                : "bg-red-100 text-red-800"
          }`}
        >
          {payment.status === "COMPLETED"
            ? "Complété"
            : payment.status === "PENDING"
              ? "En attente"
              : "Échoué"}
        </span>

        <button
          onClick={() => onViewDetails(payment)}
          className="flex items-center justify-center gap-2 px-4 py-2 text-sm bg-white border border-gray-300 hover:bg-gray-50 transition-colors"
        >
          <BiDetail size={16} />
          Détails
        </button>
      </div>
    </div>
  );
}

export default function PaiementsPage() {
  const { payments, isLoadingPayments, getPaymentsWithDateRange } =
    salesStore();

  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  // États pour les filtres de période
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [periodFilter, setPeriodFilter] = useState("all");

  // États pour le tri
  const [sortField, setSortField] = useState("created_at");
  const [sortDirection, setSortDirection] = useState("desc");

  // États pour la pagination et modal
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Chargement initial
  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      await getPaymentsWithDateRange(); // Charge tous les paiements initialement
    } catch (error) {
      console.error("Erreur lors du chargement initial:", error);
    }
  };

  // Fonction pour obtenir les dates d'une période
  const getPeriodDates = (period) => {
    const today = new Date();
    let start = new Date(today);
    let end = new Date(today);

    const formatDate = (date) => {
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, "0");
      const d = String(date.getDate()).padStart(2, "0");
      return `${y}-${m}-${d}`;
    };

    switch (period) {
      case "today":
        start.setHours(0, 0, 0, 0);
        end.setHours(23, 59, 59, 999);
        break;

      case "week":
        start.setDate(today.getDate() - today.getDay());
        start.setHours(0, 0, 0, 0);
        end.setDate(today.getDate() + (6 - today.getDay()));
        end.setHours(23, 59, 59, 999);
        break;

      case "month":
        start.setDate(1);
        start.setHours(0, 0, 0, 0);
        end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        end.setHours(23, 59, 59, 999);
        break;

      default:
        return { startDate: "", endDate: "" };
    }

    return {
      startDate: formatDate(start),
      endDate: formatDate(end),
    };
  };

  // Charger les paiements avec les filtres
  const loadPayments = async (
    period = periodFilter,
    customStartDate = startDate,
    customEndDate = endDate,
  ) => {
    try {
      let start = customStartDate;
      let end = customEndDate;

      if (period !== "custom" && period !== "all") {
        const dates = getPeriodDates(period);
        start = dates.startDate;
        end = dates.endDate;
        setStartDate(dates.startDate);
        setEndDate(dates.endDate);
      }

      if (period === "all" || !start || !end) {
        await getPaymentsWithDateRange();
      } else {
        await getPaymentsWithDateRange(start, end);
      }

      // Réinitialiser la pagination après un nouveau chargement
      setCurrentPage(1);
    } catch (error) {
      console.error("Erreur lors du chargement des paiements:", error);
    }
  };

  const handlePeriodChange = async (period) => {
    setPeriodFilter(period);

    if (period === "all") {
      setStartDate("");
      setEndDate("");
      await loadPayments("all", "", "");
    } else if (period !== "custom") {
      const dates = getPeriodDates(period);
      await loadPayments(period, dates.startDate, dates.endDate);
    }
  };

  // Effet pour les dates personnalisées
  useEffect(() => {
    if (periodFilter === "custom" && startDate && endDate) {
      const timer = setTimeout(() => {
        loadPayments("custom", startDate, endDate);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [startDate, endDate, periodFilter]);

  const resetFilters = async () => {
    setPeriodFilter("all");
    setStartDate("");
    setEndDate("");
    setSearchTerm("");
    setStatusFilter("");
    setSortField("created_at");
    setSortDirection("desc");
    await loadPayments("all", "", "");
  };

  const getPeriodLabel = () => {
    switch (periodFilter) {
      case "today":
        return "Aujourd'hui";
      case "week":
        return "Cette semaine";
      case "month":
        return "Ce mois";
      case "custom":
        return "Période personnalisée";
      default:
        return "Toutes les périodes";
    }
  };

  // Fonction pour gérer le tri
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("desc");
    }
  };

  // Fonction pour obtenir le nom du client
  const getClientName = (payment) =>
    resolveClientName(payment, "Client non spécifié");

  // Filtrage et tri des paiements
  const filteredAndSortedPayments = useMemo(() => {
    if (!payments) return [];

    let filtered = payments.filter((payment) => {
      // Filtre par recherche
      const matchesSearch =
        !searchTerm ||
        getClientName(payment)
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        payment.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        payment.amount.toString().includes(searchTerm);

      // Filtre par statut
      const matchesStatus = !statusFilter || payment.status === statusFilter;

      return matchesSearch && matchesStatus;
    });

    // Tri des résultats
    filtered.sort((a, b) => {
      let aValue, bValue;

      switch (sortField) {
        case "client":
          aValue = getClientName(a);
          bValue = getClientName(b);
          break;
        case "amount":
          aValue = parseFloat(a.amount);
          bValue = parseFloat(b.amount);
          break;
        case "method":
          aValue = a.method || "";
          bValue = b.method || "";
          break;
        case "status":
          aValue = a.status || "";
          bValue = b.status || "";
          break;
        case "created_at":
          aValue = new Date(a.created_at);
          bValue = new Date(b.created_at);
          break;
        default:
          aValue = a[sortField];
          bValue = b[sortField];
      }

      if (sortDirection === "asc") {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    return filtered;
  }, [payments, searchTerm, statusFilter, sortField, sortDirection]);

  // Fonctions pour le tableau responsive
  const handleViewDetails = (payment) => {
    setSelectedPayment(payment);
    setIsModalOpen(true);
  };

  // Pagination
  const totalPages = Math.ceil(filteredAndSortedPayments.length / itemsPerPage);
  const paginatedPayments = filteredAndSortedPayments.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  // Colonnes pour le tableau desktop
  const columns = [
    {
      headerName: "Client",
      field: "client",
      width: "25%",
      sortable: true,
      render: (payment) => {
        const clientName = getClientName(payment);
        const displayName =
          clientName.length > 25
            ? clientName.substring(0, 25) + "..."
            : clientName;

        const client = getClientContact(payment);

        return (
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span
                className="font-nunito font-normal text-sm truncate"
                title={clientName}
              >
                {displayName}
              </span>
              <div className="flex gap-1 shrink-0">
                {client?.email && (
                  <a
                    href={`mailto:${client.email}`}
                    className="text-gray-500 hover:text-gray-700"
                    title="Envoyer un email"
                  >
                    <FaEnvelope size={12} />
                  </a>
                )}
                {client?.telephone && (
                  <a
                    href={`tel:${client.telephone}`}
                    className="text-secondary-500 hover:text-secondary-700"
                    title="Appeler"
                  >
                    <BiPhone size={12} />
                  </a>
                )}
                {(client?.whatsapp || client?.telephone) && (
                  <a
                    href={getWhatsappLink(client)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-green-500 hover:text-green-700"
                    title="WhatsApp"
                  >
                    <FaWhatsapp size={12} />
                  </a>
                )}
              </div>
            </div>
            <div className="flex items-center gap-1 mt-1">
              <span className="px-1 py-0.5 text-xs bg-blue-100 text-blue-800">
                {payment.is_vente ? "Vente" : "En ligne"}
              </span>
              <span className="px-1 py-0.5 text-xs bg-purple-100 text-purple-800">
                {payment.appointment
                  ? "Réservation"
                  : payment.subscription
                    ? "Abonnement"
                    : "Direct"}
              </span>
            </div>
          </div>
        );
      },
    },
    {
      headerName: "Montant",
      field: "amount",
      width: "15%",
      sortable: true,
      render: (payment) => (
        <div className="font-poppins font-bold text-sm text-montant">
          {parseFloat(payment.amount).toLocaleString()} FCFA
        </div>
      ),
    },
    {
      headerName: "Méthode",
      field: "method",
      width: "15%",
      sortable: true,
      render: (payment) => (
        <span
          className={`px-2 py-1 text-xs ${
            payment.method === "CASH"
              ? "bg-green-100 text-green-800"
              : payment.method === "CARD"
                ? "bg-secondary-100 text-secondary-800"
                : payment.method === "FEDAPAY"
                  ? "bg-blue-100 text-blue-800"
                  : "bg-purple-100 text-purple-800"
          }`}
        >
          {payment.method}
        </span>
      ),
    },
    {
      headerName: "Statut",
      field: "status",
      width: "15%",
      sortable: true,
      render: (payment) => (
        <span
          className={`px-2 py-1 text-xs ${
            payment.status === "COMPLETED"
              ? "bg-green-100 text-green-800"
              : payment.status === "PENDING"
                ? "bg-yellow-100 text-yellow-800"
                : "bg-red-100 text-red-800"
          }`}
        >
          {payment.status === "COMPLETED"
            ? "Complété"
            : payment.status === "PENDING"
              ? "En attente"
              : "Échoué"}
        </span>
      ),
    },
    {
      headerName: "Date",
      field: "created_at",
      width: "15%",
      sortable: true,
      render: (payment) => (
        <div className="font-nunito text-xs text-text">
          {new Date(payment.created_at).toLocaleDateString("fr-FR", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
          })}
        </div>
      ),
    },
    {
      headerName: "",
      width: "15%",
      render: (payment) => (
        <button
          onClick={() => handleViewDetails(payment)}
          className="w-full px-3 py-1.5 text-xs bg-gray-100 hover:bg-gray-200 transition-colors flex items-center justify-center gap-1"
        >
          <BiDetail size={14} />
          Détails
        </button>
      ),
    },
  ];

  return (
    <div className="md:p-6">
      {/* En-tête */}
      <div className="mb-6">
        <h1 className="font-poppins font-semibold text-[18px] text-title">
          Paiements en Ligne
        </h1>
        <p className="font-nunito text-[14px] text-text mt-1">
          Gestion et suivi des paiements effectués en ligne
        </p>
      </div>

      {/* Barre de recherche et filtres */}
      <div className="bg-white p-4 shadow-sm border border-gray-200 mb-6">
        <div className="flex flex-col 2xl:flex-row gap-4">
          {/* Barre de recherche */}
          <div className="flex-1 relative">
            <BiSearch
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
              size={20}
            />
            <input
              type="text"
              placeholder="Rechercher par client ou code..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-secondary-500 font-nunito text-[14px]"
            />
          </div>

          {/* Filtres de période et statut */}
          <div className="flex flex-col xl:flex-row justify-between gap-3">
            {/* Sélecteur de période rapide */}
            {/* <select
              value={periodFilter}
              onChange={(e) => handlePeriodChange(e.target.value)}
              className="border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-secondary-500 font-nunito text-[14px]"
            >
              <option value="all">Toutes les périodes</option>
              <option value="today">Aujourd'hui</option>
              <option value="week">Cette semaine</option>
              <option value="month">Ce mois</option>
              <option value="custom">Période personnalisée</option>
            </select> */}

            {/* Dates personnalisées */}
            {/* {periodFilter === "custom" && (
              <div className="flex flex-col sm:flex-row gap-2">
                <div className="flex flex-1 justify-end items-center gap-2 bg-white border border-gray-300 px-3 py-2">
                  <BiCalendar className="text-gray-400" size={16} />
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="focus:outline-none font-nunito text-[14px] w-32"
                  />
                </div>
                <div className="flex flex-1 justify-end items-center gap-2 bg-white border border-gray-300 px-3 py-2">
                  <BiCalendar className="text-gray-400" size={16} />
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="focus:outline-none font-nunito text-[14px] w-32"
                  />
                </div>
              </div>
            )} */}

            {/* Filtre par statut */}
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-secondary-500 font-nunito text-[14px]"
            >
              <option value="">Tous les statuts</option>
              <option value="COMPLETED">Complété</option>
              <option value="PENDING">En attente</option>
              <option value="FAILED">Échoué</option>
            </select>

            {/* Bouton réinitialiser */}
            {(periodFilter !== "all" ||
              searchTerm ||
              statusFilter ||
              sortField !== "created_at" ||
              sortDirection !== "desc") && (
              <button
                onClick={resetFilters}
                className="flex items-center gap-2 px-3 py-2 font-nunito text-[14px] font-semibold text-action text-gray-600 border border-gray-300 hover:bg-gray-50"
              >
                <BiReset size={16} />
                Réinitialiser
              </button>
            )}
          </div>
        </div>

        {/* Indicateur de période active */}
        {(periodFilter !== "all" || searchTerm || statusFilter) && (
          <div className="mt-3 flex items-center gap-2 font-nunito text-[14px]">
            <span className="text-gray-600">Filtres actifs:</span>
            {periodFilter !== "all" && (
              <span className="bg-secondary-100 text-secondary-800 px-2 py-1">
                {getPeriodLabel()}
              </span>
            )}
            {searchTerm && (
              <span className="bg-green-100 text-green-800 px-2 py-1">
                Recherche: "{searchTerm}"
              </span>
            )}
            {statusFilter && (
              <span className="bg-purple-100 text-purple-800 px-2 py-1">
                Statut:{" "}
                {statusFilter === "COMPLETED"
                  ? "Complété"
                  : statusFilter === "PENDING"
                    ? "En attente"
                    : "Échoué"}
              </span>
            )}
            <span className="bg-gray-100 text-gray-800 px-2 py-1">
              {filteredAndSortedPayments.length} résultat
              {filteredAndSortedPayments.length !== 1 ? "s" : ""}
            </span>
          </div>
        )}
      </div>

      {isLoadingPayments ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-secondary-600"></div>
        </div>
      ) : (
        <>
          {/* Pagination en haut */}
          {totalPages > 1 && filteredAndSortedPayments.length > 0 && (
            <div className="mb-4 flex flex-col sm:flex-row items-center justify-between gap-4 bg-white p-4 border border-gray-200">
              <div className="font-nunito text-xs text-text">
                Page {currentPage} sur {totalPages} •{" "}
                {filteredAndSortedPayments.length} élément
                {filteredAndSortedPayments.length > 1 ? "s" : ""}
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(prev - 1, 1))
                  }
                  disabled={currentPage === 1}
                  className="p-2 border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  <BiChevronLeft size={18} />
                </button>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }

                  return (
                    <button
                      key={i}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`w-8 h-8 text-xs font-nunito ${
                        currentPage === pageNum
                          ? "bg-secondary-600 text-white"
                          : "border border-gray-300 hover:bg-gray-50"
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                  }
                  disabled={currentPage === totalPages}
                  className="p-2 border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  <BiChevronRight size={18} />
                </button>
              </div>
            </div>
          )}

          {/* Tableau Desktop */}
          <div className="hidden lg:block bg-white border border-gray-200 overflow-hidden mb-6">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    {columns.map((column, index) => (
                      <th
                        key={index}
                        className="px-4 py-3 text-left font-poppins font-bold text-xs text-title border-b border-gray-200"
                        style={{ width: column.width }}
                      >
                        {column.sortable ? (
                          <button
                            onClick={() => handleSort(column.field)}
                            className="flex items-center gap-1 hover:text-secondary-600"
                          >
                            {column.headerName}
                            {sortField === column.field && (
                              <span className="text-xs">
                                {sortDirection === "asc" ? "↑" : "↓"}
                              </span>
                            )}
                          </button>
                        ) : (
                          column.headerName
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y font-poppins divide-gray-200">
                  {paginatedPayments.map((payment, index) => (
                    <tr
                      key={payment.id}
                      className={
                        index % 2 === 0
                          ? "bg-white"
                          : "bg-gray-50 hover:bg-gray-100"
                      }
                    >
                      {columns.map((column, colIndex) => (
                        <td key={colIndex} className="px-4 py-3">
                          <div className="flex items-center h-full">
                            {column.render(payment)}
                          </div>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Cartes Mobile */}
          <div className="lg:hidden space-y-3 mb-6">
            {paginatedPayments.map((payment) => (
              <PaymentCard
                key={payment.id}
                payment={payment}
                onViewDetails={handleViewDetails}
              />
            ))}
          </div>

          {/* Pagination en bas */}
          {totalPages > 1 && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-white p-4 border border-gray-200">
              <div className="font-nunito text-xs text-text">
                Page {currentPage} sur {totalPages} •{" "}
                {filteredAndSortedPayments.length} élément
                {filteredAndSortedPayments.length > 1 ? "s" : ""}
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(prev - 1, 1))
                  }
                  disabled={currentPage === 1}
                  className="p-2 border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  <BiChevronLeft size={18} />
                </button>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }

                  return (
                    <button
                      key={i}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`w-8 h-8 text-xs font-nunito ${
                        currentPage === pageNum
                          ? "bg-secondary-600 text-white"
                          : "border border-gray-300 hover:bg-gray-50"
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                  }
                  disabled={currentPage === totalPages}
                  className="p-2 border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  <BiChevronRight size={18} />
                </button>
              </div>
            </div>
          )}

          {/* Message vide */}
          {filteredAndSortedPayments.length === 0 && (
            <div className="text-center py-12 bg-white border border-gray-200">
              <BiCreditCard className="mx-auto text-gray-300" size={40} />
              <p className="font-nunito text-sm text-text mt-3">
                {searchTerm || statusFilter || periodFilter !== "all"
                  ? "Aucun résultat trouvé"
                  : "Aucun paiement pour cette période"}
              </p>
            </div>
          )}
        </>
      )}

      {/* Modal de détails */}
      <PaymentDetailModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedPayment(null);
        }}
        payment={selectedPayment}
      />
    </div>
  );
}
